// =============================================================================
// Phase 1 SOC / Wazuh Integration: Create Endpoint from Discovery Dialog
// =============================================================================

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityStore } from '@/stores/securityStore';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { DiscoveredIdentity } from '@/types/security';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discovery: DiscoveredIdentity;
}

interface FormData {
  name: string;
  type: string;
  site_id: string;
  system_id: string;
}

interface Site { id: string; name: string; }
interface System { id: string; name: string; }

const ENDPOINT_TYPES = ['server', 'workstation', 'plc', 'hmi', 'switch', 'router', 'firewall', 'sensor', 'actuator', 'gateway', 'other'];

export function CreateEndpointFromDiscoveryDialog({ open, onOpenChange, discovery }: Props) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  const { createEndpointFromDiscovery } = useSecurityStore();
  
  const [sites, setSites] = useState<Site[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [creating, setCreating] = useState(false);
  
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { name: '', type: 'server', site_id: '', system_id: '' },
  });
  
  useEffect(() => {
    if (orgId && open) {
      supabase.from('sites').select('id, name').eq('org_id', orgId).then(({ data }) => setSites(data || []));
      supabase.from('systems').select('id, name').eq('org_id', orgId).then(({ data }) => setSystems(data || []));
    }
  }, [orgId, open]);
  
  useEffect(() => {
    if (open && discovery) {
      reset({ name: discovery.hostname || discovery.ip || '', type: 'server', site_id: discovery.site_id || '', system_id: '' });
    }
  }, [open, discovery, reset]);
  
  const onSubmit = async (data: FormData) => {
    setCreating(true);
    try {
      await createEndpointFromDiscovery(discovery.id, {
        name: data.name, type: data.type,
        site_id: data.site_id || undefined, system_id: data.system_id || undefined,
      });
      toast.success('Endpoint created');
      onOpenChange(false);
    } catch {
      toast.error('Failed to create endpoint');
    } finally {
      setCreating(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" />Create Endpoint from Discovery</DialogTitle>
          <DialogDescription>Create a new endpoint from this discovered network identity.</DialogDescription>
        </DialogHeader>
        
        <div className="rounded-lg border p-3 bg-muted/50 space-y-1 text-sm">
          <div><span className="text-muted-foreground">IP:</span> <span className="font-mono">{discovery.ip || '-'}</span></div>
          <div><span className="text-muted-foreground">MAC:</span> <span className="font-mono">{discovery.mac || '-'}</span></div>
          <div><span className="text-muted-foreground">Hostname:</span> {discovery.hostname || '-'}</div>
          <div><span className="text-muted-foreground">Source:</span> <Badge variant="outline" className="text-xs">{discovery.source}</Badge></div>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Endpoint Name *</Label>
            <Input {...register('name', { required: 'Required' })} placeholder="e.g., Production Server 1" />
            {errors.name && <p className="text-xs text-danger">{errors.name.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={watch('type')} onValueChange={(v) => setValue('type', v)}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {ENDPOINT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Site (Optional)</Label>
            <Select value={watch('site_id') || 'none'} onValueChange={(v) => setValue('site_id', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No site</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>System (Optional)</Label>
            <Select value={watch('system_id') || 'none'} onValueChange={(v) => setValue('system_id', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select system" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No system</SelectItem>
                {systems.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Endpoint
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
